import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type CurrentAndLastMonth = {
  LastMonthSales: number;
  CurrentMonthSales: number;
};

export type WeeklyChartPoint = {
  week: string | number;
  ThisMonth: number;
  LastMonth: number;
};

export type RevenueOverTimePoint = {
  bucket: string;
  label: string;
  revenue: number;
};

export type RevenueOverTimeState = {
  series: RevenueOverTimePoint[];
  totalRevenue: number;
  bucket: string;
  requestedBucket: string;
  coarsened: boolean;
  timezone: string;
  startDate: string;
  endDate: string;
};

export interface ReportsSliceObject {
  currentAndLastMonth: CurrentAndLastMonth;
  dailyPayments: Array<{ moneyIn: number; time: string; sortTime: number }>;
  revenueOverTime: RevenueOverTimeState;
  weeklySalesData: {
    chartData: WeeklyChartPoint[];
    thisMonthTotalSales: number;
    lastMonthTotalSales: number;
  };
  // Web-parity storage for Opening & Closing Stocks report
  productsReport: {
    ProductsReport: any[];
    totalDocs: number;
    totalPages?: number;
    currentPage?: number;
    perPage?: number;
    metadata: {
      totalAmountWasted: number;
      totalItemsSold: number;
      totalItemsWasted: number;
      totalSalesMade: number;
      totalUpdatedProducts: number;
      totalUpdatedProductsAmount: number;
    };
  };
}

const emptyRevenueOverTime: RevenueOverTimeState = {
  series: [],
  totalRevenue: 0,
  bucket: '',
  requestedBucket: '',
  coarsened: false,
  timezone: '',
  startDate: '',
  endDate: '',
};

const initialState: ReportsSliceObject = {
  currentAndLastMonth: { LastMonthSales: 0, CurrentMonthSales: 0 },
  dailyPayments: [],
  revenueOverTime: emptyRevenueOverTime,
  weeklySalesData: {
    chartData: [],
    thisMonthTotalSales: 0,
    lastMonthTotalSales: 0,
  },
  productsReport: {
    ProductsReport: [],
    totalDocs: 0,
    totalPages: 0,
    currentPage: 0,
    perPage: 0,
    metadata: {
      totalAmountWasted: 0,
      totalItemsSold: 0,
      totalItemsWasted: 0,
      totalSalesMade: 0,
      totalUpdatedProducts: 0,
      totalUpdatedProductsAmount: 0,
    },
  },
};

const reportsSlice = createSlice({
  name: 'backofficeReports',
  initialState,
  reducers: {
    setCurrentAndLastMonth: (
      state,
      action: PayloadAction<{
        lastMonth: { totalSales: number };
        currentMonth: { totalSales: number };
      }>,
    ) => {
      state.currentAndLastMonth = {
        LastMonthSales: action.payload.lastMonth.totalSales,
        CurrentMonthSales: action.payload.currentMonth.totalSales,
      };
    },
    setDailyPayments: (
      state,
      action: PayloadAction<Array<{ moneyIn: number; time: string | number }>>,
    ) => {
      const transformed = action.payload.map(d => ({
        moneyIn: d.moneyIn,
        time: typeof d.time === 'string' ? d.time : String(d.time),
        sortTime: new Date(d.time as any).getTime(),
      }));
      transformed.sort((a, b) => a.sortTime - b.sortTime);
      state.dailyPayments = transformed;
    },
    setRevenueOverTime: (
      state,
      action: PayloadAction<Partial<RevenueOverTimeState> | null | undefined>,
    ) => {
      const payload = action.payload || {};
      state.revenueOverTime = {
        series: Array.isArray(payload.series)
          ? payload.series.map(p => ({
              bucket: String(p?.bucket ?? ''),
              label: String(p?.label ?? ''),
              revenue: Number(p?.revenue ?? 0),
            }))
          : [],
        totalRevenue: Number(payload.totalRevenue ?? 0),
        bucket: String(payload.bucket ?? ''),
        requestedBucket: String(payload.requestedBucket ?? ''),
        coarsened: Boolean(payload.coarsened),
        timezone: String(payload.timezone ?? ''),
        startDate: String(payload.startDate ?? ''),
        endDate: String(payload.endDate ?? ''),
      };
    },
    setWeeklySalesData: (
      state,
      action: PayloadAction<
        Array<{
          week: string | number;
          currentMonthSales: { sales: number };
          previousMonthSales: { sales: number };
        }>
      >,
    ) => {
      const chart = action.payload.map(p => ({
        week: p.week,
        ThisMonth: p.currentMonthSales.sales,
        LastMonth: p.previousMonthSales.sales,
      }));
      const thisMonthTotalSales = chart.reduce(
        (acc, c) => acc + c.ThisMonth,
        0,
      );
      const lastMonthTotalSales = chart.reduce(
        (acc, c) => acc + c.LastMonth,
        0,
      );
      state.weeklySalesData = {
        chartData: chart,
        thisMonthTotalSales,
        lastMonthTotalSales,
      };
    },
    // Store opening/closing stocks report exactly like web
    setProductsReport: (state, action: PayloadAction<any>) => {
      const payload = action.payload || {};
      state.productsReport = {
        ProductsReport: payload.docs || payload.data || [],
        totalDocs: payload.totalDocs ?? payload.total ?? 0,
        totalPages: payload.totalPages ?? 1,
        currentPage: payload.page ?? payload.currentPage ?? 1,
        perPage: payload.limit ?? payload.perPage ?? 0,
        metadata: {
          totalItemsSold: payload?.metadata?.totalItemsSold ?? 0,
          totalAmountWasted: payload?.metadata?.totalAmountWasted ?? 0,
          totalItemsWasted: payload?.metadata?.totalItemsWasted ?? 0,
          totalUpdatedProducts: payload?.metadata?.totalUpdatedProducts ?? 0,
          totalUpdatedProductsAmount:
            payload?.metadata?.totalUpdatedProductsAmount ?? 0,
          totalSalesMade: payload?.metadata?.totalSalesMade ?? 0,
        },
      } as ReportsSliceObject['productsReport'];
    },
  },
});

export const {
  setCurrentAndLastMonth,
  setDailyPayments,
  setRevenueOverTime,
  setWeeklySalesData,
  setProductsReport,
} = reportsSlice.actions;
export default reportsSlice.reducer;
